/**
 * Altura Global — Interactive World Map.
 *
 * SVG-based, no tile provider needed. Countries are colored on a log-ish scale
 * of visitor volume, hover surfaces a tooltip with the exact count + rank, and
 * click can optionally filter the dashboard.
 *
 * Uses a locally-hosted TopoJSON atlas so we never depend on a third-party CDN
 * at runtime (loaded once from /data/world-countries-110m.json).
 */
import { useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { scaleSequentialLog } from "d3-scale";
import { interpolateRgb } from "d3-interpolate";

const GEO_URL = "/data/world-countries-110m.json";

// GA4 → world-atlas name reconciliation. Add entries only when GA4 emits a
// name that differs from the atlas' `properties.name`.
const NAME_MAP = {
  "United States": "United States of America",
  "USA": "United States of America",
  "U.S.": "United States of America",
  "UK": "United Kingdom",
  "Great Britain": "United Kingdom",
  "England": "United Kingdom",
  "South Korea": "Korea",
  "North Korea": "Dem. Rep. Korea",
  "Democratic Republic of the Congo": "Dem. Rep. Congo",
  "Republic of the Congo": "Congo",
  "Ivory Coast": "Côte d'Ivoire",
  "Cote d'Ivoire": "Côte d'Ivoire",
  "Czechia": "Czech Rep.",
  "Bosnia and Herzegovina": "Bosnia and Herz.",
  "Dominican Republic": "Dominican Rep.",
  "Central African Republic": "Central African Rep.",
  "Equatorial Guinea": "Eq. Guinea",
  "Solomon Islands": "Solomon Is.",
  "South Sudan": "S. Sudan",
  "Falkland Islands": "Falkland Is.",
  "Taiwan": "Taiwan",
};

function normalize(name) {
  if (!name) return "";
  return NAME_MAP[name] || name;
}

export default function WorldMap({
  rows = [],
  height = 420,
  onCountryClick,
  activeCountry,
  emptyLabel = "No visitor data yet in this range.",
  testid = "world-map",
}) {
  const [tooltip, setTooltip] = useState(null);

  // Build a canonical name → visitors lookup.
  const { byName, max, total, ranked } = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = normalize(r.country);
      if (!key) continue;
      map.set(key, (map.get(key) || 0) + (r.visitors || 0));
    }
    const maxV = Math.max(1, ...map.values());
    const tot = [...map.values()].reduce((s, v) => s + v, 0);
    const sorted = [...map.entries()].sort((a, b) => b[1] - a[1]);
    return { byName: map, max: maxV, total: tot, ranked: sorted };
  }, [rows]);

  // Log color scale so a handful of dominant countries don't wash out the tail.
  const color = useMemo(() => {
    return scaleSequentialLog(interpolateRgb("#F3EAD7", "#3A1F14"))
      .domain([0.9, Math.max(2, max)]);
  }, [max]);

  const rankByName = useMemo(() => {
    const m = new Map();
    ranked.forEach(([name], i) => m.set(name, i + 1));
    return m;
  }, [ranked]);

  return (
    <div className="relative" data-testid={testid}>
      <div style={{ height }} className="w-full overflow-hidden rounded-md bg-altura-ivory-2/40 border border-altura-espresso/10">
        <ComposableMap
          projection="geoEqualEarth"
          projectionConfig={{ scale: 160 }}
          style={{ width: "100%", height: "100%" }}
        >
          <ZoomableGroup center={[0, 15]} zoom={1} minZoom={1} maxZoom={4}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const name = geo.properties?.name || "";
                  const visitors = byName.get(name) || 0;
                  const isActive = activeCountry && normalize(activeCountry) === name;
                  const fill = visitors ? color(Math.max(1, visitors)) : "#EFE7D8";
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={(e) => setTooltip({ name, visitors, rank: rankByName.get(name), x: e.clientX, y: e.clientY })}
                      onMouseMove={(e) => setTooltip((t) => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                      onMouseLeave={() => setTooltip(null)}
                      onClick={() => onCountryClick && visitors && onCountryClick(name)}
                      style={{
                        default: {
                          fill,
                          stroke: "#D8CFC0",
                          strokeWidth: 0.5,
                          outline: "none",
                          cursor: visitors && onCountryClick ? "pointer" : "default",
                        },
                        hover: {
                          fill: visitors ? "#C9A45A" : "#E4DBC9",
                          stroke: "#3A1F14",
                          strokeWidth: 0.6,
                          outline: "none",
                        },
                        pressed: { fill: "#C9A45A", outline: "none" },
                      }}
                      // Ring the active country if provided.
                      className={isActive ? "ring-2 ring-altura-gold" : undefined}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </div>

      {/* Legend + top-country list */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-altura-charcoal/60">
          <span>Fewer</span>
          <span className="inline-block h-2 w-24 rounded-full" style={{ background: "linear-gradient(90deg,#F3EAD7,#3A1F14)" }} />
          <span>More</span>
          {total > 0 && (
            <span className="ml-3 text-altura-charcoal/70 tracking-normal normal-case">
              {byName.size} {byName.size === 1 ? "country" : "countries"} · {total.toLocaleString()} visitors
            </span>
          )}
        </div>
      </div>

      {byName.size === 0 && (
        <div className="mt-2 text-xs text-altura-charcoal/50">{emptyLabel}</div>
      )}

      {tooltip && (
        <div
          role="tooltip"
          className="pointer-events-none fixed z-50 rounded-md border border-altura-espresso/20 bg-white/95 backdrop-blur-sm px-3 py-2 shadow-lg text-xs"
          style={{ left: tooltip.x + 12, top: tooltip.y + 12 }}
        >
          <div className="font-editorial text-sm text-altura-espresso">{tooltip.name || "—"}</div>
          {tooltip.visitors > 0 ? (
            <div className="text-altura-charcoal/70 mt-0.5">
              {tooltip.visitors.toLocaleString()} visitor{tooltip.visitors === 1 ? "" : "s"}
              {tooltip.rank ? ` · rank #${tooltip.rank}` : ""}
            </div>
          ) : (
            <div className="text-altura-charcoal/50 mt-0.5">No visitors yet</div>
          )}
        </div>
      )}
    </div>
  );
}

/** Utility: given a list of {country, visitors}, produce a top-N ranking. */
export function topCountries(rows, limit = 8) {
  const byName = new Map();
  for (const r of rows || []) {
    const k = r.country || "(unknown)";
    byName.set(k, (byName.get(k) || 0) + (r.visitors || 0));
  }
  return [...byName.entries()]
    .map(([country, visitors]) => ({ country, visitors }))
    .sort((a, b) => b.visitors - a.visitors)
    .slice(0, limit);
}
